import { Box, Button, ButtonIcon, ButtonText, DownloadIcon, HStack, Pressable, Text, VStack } from '@gluestack-ui/themed';
import { useContext } from 'react';
import { ProgressBar } from '../ProgressBar';
import { TranslationContext } from '../../../contexts/translations/Translation';
import { PrimaryButton } from '../../buttons/PrimaryButton';
import { COLORS } from '../../../theme/colors';

const ORGANISM_CONFIG: Record<string, { letter: string; detectedKey: string; morphologyKey?: string; avatarColor: string }> = {
  bacteria: { letter: 'B', detectedKey: 'bacteria_detected', morphologyKey: 'bacteria_morphology', avatarColor: '#2A464D' },
  yeast:    { letter: 'L', detectedKey: 'yeast_detected', avatarColor: '#7FC9AF' },
  mold:     { letter: 'M', detectedKey: 'mold_detected', avatarColor: '#C7E7F1' },
};

type BinaryDecision = 'PRESENCE' | 'ABSENCE' | 'INCERTAIN';

const DECISION_CONFIG: Record<BinaryDecision, { color: string; labelKey: string }> = {
  PRESENCE:  { color: COLORS.ACCENT[100],       labelKey: 'page.analysis-details.detection.decision.presence' },
  ABSENCE:   { color: COLORS.STATUS.SUCCESS,    labelKey: 'page.analysis-details.detection.decision.absence' },
  INCERTAIN: { color: '#F59E0B',                labelKey: 'page.analysis-details.detection.decision.incertain' },
};

interface IDetectionResultsProps {
  results: Record<string, number | string>;
  selectedOrganisms: string[];
  onInvestigate: () => void;
  onDownloadReport: () => void;
  onBack: () => void;
  isInvestigateLoading?: boolean;
  // hideActions: render the result cards without the action buttons.
  // actionsOnly: render only the action buttons (so they can sit at the very bottom).
  hideActions?: boolean;
  actionsOnly?: boolean;
  testID?: string;
}

function isBinaryFormat(results: Record<string, number | string>): boolean {
  return 'bacteria_decision' in results || 'yeast_decision' in results || 'mold_decision' in results;
}

export const DetectionResults = ({
  results,
  selectedOrganisms,
  onInvestigate,
  onDownloadReport,
  onBack,
  isInvestigateLoading,
  hideActions,
  actionsOnly,
  testID,
}: IDetectionResultsProps) => {
  const { i18n } = useContext(TranslationContext);

  const isBacteriaSelected = selectedOrganisms.includes('bacteria');
  const useBinaryFormat = isBinaryFormat(results);
  const isUnknownImage = results['verdict_status'] === 'unknown';
  const isQualityFail = results['verdict_status'] === 'quality_fail';
  const isBacteriaDetected = useBinaryFormat
    ? results['bacteria_decision'] === 'PRESENCE'
    : (results['bacteria_detected'] as number) >= 0.5;

  const actionsBlock = (
    <VStack gap={8} marginTop={4}>
      {isBacteriaSelected && isBacteriaDetected && (
        <PrimaryButton
          onPress={async () => onInvestigate()}
          content={i18n?.t('page.analysis-details.detection.investigate-button')}
          isLoading={isInvestigateLoading}
          width="100%"
          minWidth={0}
        />
      )}
      <Button
        backgroundColor="transparent"
        borderWidth={1}
        borderColor={COLORS.NEUTRAL[70]}
        borderRadius={10}
        width="100%"
        onPress={onDownloadReport}
      >
        <ButtonText style={{ color: COLORS.FOUNDATION_TERCIARY_100 }} paddingRight={4}>
          {i18n?.t('page.analysis-details.detection.download-report-button')}
        </ButtonText>
        <ButtonIcon as={DownloadIcon} color={COLORS.FOUNDATION_TERCIARY_100} />
      </Button>
      <Pressable onPress={onBack} alignSelf="center" marginTop={4}>
        <Text fontSize={13} color={COLORS.ACCENT[100]}>
          {i18n?.t('common.back')}
        </Text>
      </Pressable>
    </VStack>
  );

  if (actionsOnly) return actionsBlock;

  return (
    <VStack gap={12}>
      <Text fontWeight="bold" fontSize={16}>
        {i18n?.t('page.analysis-details.detection.title')}
      </Text>

      {isQualityFail && (
        <Box
          style={{
            borderRadius: 10,
            backgroundColor: '#FEE2E2',
            borderWidth: 1,
            borderColor: '#EF4444',
            padding: 12,
          }}
        >
          <Text fontSize={13} color="#991B1B" fontWeight="bold">
            {i18n?.t('page.analysis-details.detection.quality-fail-warning')}
          </Text>
        </Box>
      )}

      {isUnknownImage && (
        <Box
          style={{
            borderRadius: 10,
            backgroundColor: '#FEF3C7',
            borderWidth: 1,
            borderColor: '#F59E0B',
            padding: 12,
          }}
        >
          <Text fontSize={13} color="#92400E" fontWeight="bold">
            {i18n?.t('page.analysis-details.detection.unknown-image-warning')}
          </Text>
        </Box>
      )}

      {selectedOrganisms.map((organism) => {
        const config = ORGANISM_CONFIG[organism];
        if (!config) return null;

        if (useBinaryFormat) {
          const decision = (results[`${organism}_decision`] as BinaryDecision) ?? 'INCERTAIN';
          const proba = (results[`${organism}_proba`] as number) ?? 0;
          const decisionConfig = DECISION_CONFIG[decision] ?? DECISION_CONFIG['INCERTAIN'];

          return (
            <Box
              key={organism}
              style={{
                borderRadius: 10,
                backgroundColor: COLORS.NEUTRAL[0],
                borderWidth: 1,
                borderColor: COLORS.NEUTRAL[70],
                padding: 14,
              }}
              testID={`${testID ?? ''}detection_${organism}`}
            >
              <HStack gap={12} alignItems="center">
                <Box
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 16,
                    backgroundColor: config.avatarColor,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Text fontWeight="bold" color={COLORS.NEUTRAL[0]} fontSize={15}>{config.letter}</Text>
                </Box>

                <VStack flex={1} gap={6}>
                  <HStack justifyContent="space-between" alignItems="center">
                    <Text fontWeight="bold" fontSize={14}>
                      {i18n?.t(`page.analysis-details.organism-selection.${organism}.label`)}
                    </Text>
                    <HStack alignItems="center" gap={4}>
                      <Box
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: decisionConfig.color,
                        }}
                      />
                      <Text fontSize={13} fontWeight="bold" color={decisionConfig.color}>
                        {i18n?.t(decisionConfig.labelKey) ?? decision}
                      </Text>
                    </HStack>
                  </HStack>

                  <ProgressBar progress={proba} />

                  <Text fontSize={12} color={COLORS.NEUTRAL[90]}>
                    {i18n?.t('page.analysis-details.detection.confidence-label', { value: parseFloat((proba * 100).toFixed(1)) })}
                  </Text>
                </VStack>
              </HStack>
            </Box>
          );
        }

        // Legacy format: bacteria_detected / yeast_detected / mold_detected (0–1 score)
        const score = (results[config.detectedKey] as number) ?? 0;
        const detected = score >= 0.5;
        const morphology = config.morphologyKey ? (results[config.morphologyKey] as string) : undefined;

        return (
          <Box
            key={organism}
            style={{
              borderRadius: 10,
              backgroundColor: COLORS.NEUTRAL[0],
              borderWidth: 1,
              borderColor: COLORS.NEUTRAL[70],
              padding: 14,
            }}
            testID={`${testID ?? ''}detection_${organism}`}
          >
            <HStack gap={12} alignItems="center">
              <Box
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 16,
                  backgroundColor: config.avatarColor,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Text fontWeight="bold" color={COLORS.NEUTRAL[0]} fontSize={15}>{config.letter}</Text>
              </Box>

              <VStack flex={1} gap={6}>
                <HStack justifyContent="space-between" alignItems="center">
                  <Text fontWeight="bold" fontSize={14}>
                    {i18n?.t(`page.analysis-details.organism-selection.${organism}.label`)}
                  </Text>
                  <HStack alignItems="center" gap={4}>
                    <Box
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: detected ? COLORS.ACCENT[100] : COLORS.STATUS.SUCCESS,
                      }}
                    />
                    <Text
                      fontSize={13}
                      fontWeight="bold"
                      color={detected ? COLORS.ACCENT[100] : COLORS.STATUS.SUCCESS}
                    >
                      {detected
                        ? i18n?.t('page.analysis-details.detection.detected')
                        : i18n?.t('page.analysis-details.detection.not-detected')}
                    </Text>
                  </HStack>
                </HStack>

                <ProgressBar progress={score} />

                <HStack justifyContent="space-between">
                  <Text fontSize={12} color={COLORS.NEUTRAL[90]}>
                    {i18n?.t('page.analysis-details.detection.confidence-label', { value: parseFloat((score * 100).toFixed(1)) })}
                  </Text>
                  {detected && morphology ? (
                    <Text fontSize={12} color={COLORS.NEUTRAL[90]} fontStyle="italic">
                      {morphology}
                    </Text>
                  ) : null}
                </HStack>
              </VStack>
            </HStack>
          </Box>
        );
      })}

      {!hideActions && actionsBlock}
    </VStack>
  );
};
